import { useEffect, useMemo, useRef, useState } from "react";
import type { StreamItem } from "../../types/meeting.types";
import { BigVideo } from "./BigVideo";
import { VideoTile } from "./VideoTile";
import { getParticipantDisplayName } from "../../utils/participantName";
import { useMeetingStore } from "../../store/meeting.store";
import { socket } from "../mediasoup/socket";
import { recordingSocket } from "../mediasoup/recordingSocket";
import logger from "../../utils/logger";
import { formatRecTime } from "../../utils/Date";
import { toLocalTime } from "../../utils/serverClock";
import avatarImg from "../../assets/judge.jpg";
import silhouette from "../../assets/silhouette.jpg";
import type { RaisedHand } from "../../hooks/useRaisedHands";

interface Props {
  readonly streams: StreamItem[];
  readonly raisedHands?: RaisedHand[];
}

interface DrawAutoBoxOptions {
  ctx: CanvasRenderingContext2D;
  text: string | string[];
  x: number;
  y: number;
  font: string;
  paddingX?: number;
  paddingY?: number;
  radius?: number;
  bg?: string;
  color?: string;
  lineGap?: number;
}

interface TileDimensions {
  x: number;
  y: number;
  tileW: number;
  tileH: number;
}

type FrozenFramesRef = { current: Map<number, HTMLCanvasElement> };

/**
 * Grid order for every viewer: the judge's camera first, then the other two
 * courtroom cameras on the judge's device (Petitioner, Respondent), then all
 * remaining participants in join order (sort is stable).
 */
const COURT_ROLE_ORDER: Record<string, number> = {
  Judge: 0,
  Prosecutor: 1,
  Defendant: 2,
};

const sortByCourtRole = (items: StreamItem[]) =>
  [...items].sort(
    (a, b) =>
      (COURT_ROLE_ORDER[a.role ?? ""] ?? Number.MAX_SAFE_INTEGER) -
      (COURT_ROLE_ORDER[b.role ?? ""] ?? Number.MAX_SAFE_INTEGER),
  );

export function VideoGrid({ streams: rawStreams, raisedHands = [] }: Props) {
  const streams = useMemo(() => sortByCourtRole(rawStreams), [rawStreams]);
  const spotlightStream = streams.find((s) => s.spotlight);
  const otherStreams = streams.filter((s) => !s.spotlight);
  const [recSeconds, setRecSeconds] = useState(0);
  const [activeDeviceSelector, setActiveDeviceSelector] = useState<
    string | null
  >(null);
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Refs ────────────────────────────────────────────────────────────────
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const logoRef = useRef<HTMLImageElement | null>(null);
  const judgeAvatarRef = useRef<HTMLImageElement | null>(null);
  const silhouetteAvatarRef = useRef<HTMLImageElement | null>(null);
  const tickWorkerRef = useRef<Worker | null>(null);
  const drawFrameRef = useRef<() => void>(() => {});
  const streamsRef = useRef<StreamItem[]>(streams);
  const frozenFramesRef = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const recording = useMeetingStore((s) => s.recording);
  const recordingStartedAt = useMeetingStore((s) => s.recordingStartedAt);
  const isStreaming = useMeetingStore((s) => s.isStreaming);
  const setIsStreaming = useMeetingStore((s) => s.setIsStreaming);
  const setActiveRecordingCnr = useMeetingStore((s) => s.setActiveRecordingCnr);
  const setRecordingStartedAt = useMeetingStore((s) => s.setRecordingStartedAt);
  const setStreamingStartedAt = useMeetingStore((s) => s.setStreamingStartedAt);

  /**
   * AudioContext lives for the entire recording session.
   * It is created ONCE in startRecording and never recreated.
   * Recreating it resets its internal clock → DTS goes backward → FFmpeg error.
   */
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);

  /**
   * Track which video elements are already connected to the AudioContext.
   * Key = video element reference, Value = AudioNode source.
   * This prevents duplicate connections when streams state updates.
   */
  const audioSourcesRef = useRef<
    Map<
      HTMLVideoElement,
      {
        source: MediaStreamAudioSourceNode;
        gain: GainNode;
      }
    >
  >(new Map());

  // ─── Store ───────────────────────────────────────────────────────────────
  const userData = useMeetingStore((s) => s.userData);
  const setRecording = useMeetingStore((s) => s.setRecording);
  const courtRoomData = useMeetingStore((s) => s.courtRoomData);
  const selectedCase = useMeetingStore((s) => s.selectedCase);
  const selectedCaseRef = useRef(selectedCase);
  const userRoleRef = useRef(userData?.role);
  const courtRoomDataRef = useRef(courtRoomData);

  // Active recording session params (needed for socket reconnect recovery)
  const activeSessionRef = useRef<{
    cnr: string;
    roomId: string;
    courtRoomNo: string;
    actionBy: string;
  } | null>(null);

  const width = 1920;
  const height = 1080;
  const FPS = 30;

  // ─── Keep refs in sync ───────────────────────────────────────────────────
  useEffect(() => {
    streamsRef.current = streams;
  }, [streams]);
  useEffect(() => {
    selectedCaseRef.current = selectedCase;
  }, [selectedCase]);
  useEffect(() => {
    userRoleRef.current = userData?.role;
  }, [userData?.role]);
  useEffect(() => {
    courtRoomDataRef.current = courtRoomData;
  }, [courtRoomData]);

  // ─── Logo preload ────────────────────────────────────────────────────────
  useEffect(() => {
    const img = new Image();
    img.src = "/logo.png";
    logoRef.current = img;
  }, []);

  // ─── Avatar preload ─────────────────────────────────────────────────────
  useEffect(() => {
    const j = new Image();
    j.src = avatarImg;
    judgeAvatarRef.current = j;

    const s = new Image();
    s.src = silhouette;
    silhouetteAvatarRef.current = s;
  }, []);

  // ─── Audio mixing ─────────────────────────────────────────────────────────
  /**
   * Connect any NEW video elements to the existing AudioContext.
   * Called after streams update — does NOT recreate the AudioContext.
   * Safe to call multiple times; already-connected elements are skipped.
   */
  const syncAudioSources = () => {
    const ctx = audioCtxRef.current;
    const dest = audioDestRef.current;
    if (!ctx || !dest) return;

    if (ctx.state === "suspended") void ctx.resume();

    const currentStreams = streamsRef.current;
    const currentVideos = videoRefs.current.filter(Boolean);

    currentVideos.forEach((video, i) => {
      const stream = currentStreams[i];
      const srcObject = video.srcObject as MediaStream | null;
      if (!srcObject) return;

      const audioTracks = srcObject.getAudioTracks();
      if (audioTracks.length === 0) return;

      const existing = audioSourcesRef.current.get(video);

      if (existing) {
        // ✅ FIX: Never disconnect — just set gain to 0 or 1
        // This keeps the audio graph intact with no gaps
        existing.gain.gain.value = stream?.audioPaused ? 0 : 1;
        return;
      }

      // New video element — create source + gain node and connect
      try {
        const source = ctx.createMediaStreamSource(
          new MediaStream(audioTracks),
        );
        const gainNode = ctx.createGain();

        // Set initial gain based on current mute state
        gainNode.gain.value = stream?.audioPaused ? 0 : 1;

        source.connect(gainNode);
        gainNode.connect(dest);

        audioSourcesRef.current.set(video, { source, gain: gainNode });
      } catch (err) {
        console.warn("[AudioContext] failed to connect source:", err);
      }
    });

    // Disconnect sources for video elements that no longer exist
    // Only do this when element is actually gone — not for mute
    audioSourcesRef.current.forEach((nodes, video) => {
      if (!currentVideos.includes(video)) {
        try {
          nodes.gain.disconnect();
          nodes.source.disconnect();
        } catch {}
        audioSourcesRef.current.delete(video);
      }
    });
  };

  /**
   * Re-sync audio whenever streams change (camera toggle, participant join/leave).
   * This keeps the audio mix current WITHOUT touching the AudioContext clock.
   */
  useEffect(() => {
    if (!audioCtxRef.current) return; // not recording yet
    syncAudioSources();
  }, [streams]);

  // ─── Canvas helpers ───────────────────────────────────────────────────────
  const getISTDateTime = () => {
    let dt = new Date().toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return dt.replace("AM", "Am").replace("PM", "Pm");
  };

  const roundedRectPath = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r = 10,
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  const drawAutoBox = ({
    ctx,
    text,
    x,
    y,
    font,
    paddingX = 20,
    paddingY = 10,
    radius = 8,
    bg = "rgba(0,0,0,0.55)",
    color = "#D3D3D3",
    lineGap = 6,
  }: DrawAutoBoxOptions) => {
    const lines = (Array.isArray(text) ? text : [text]).filter(Boolean);
    if (lines.length === 0) return;

    ctx.font = font;
    const lineHeight = Number.parseInt(font) + lineGap;
    const textWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));
    const boxWidth = Math.max(textWidth + paddingX * 2, radius * 2 + 10);
    const boxHeight = lines.length * lineHeight + paddingY * 2 - lineGap;

    roundedRectPath(ctx, x, y, boxWidth, boxHeight, radius);
    ctx.fillStyle = bg;
    ctx.fill();

    ctx.fillStyle = color;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    lines.forEach((line, i) => {
      ctx.fillText(
        line,
        x + paddingX,
        y + paddingY + lineHeight * i + lineHeight / 2 - lineGap / 2,
      );
    });

    return { boxWidth, boxHeight };
  };

  const drawGradientPlaceholder = (
    ctx: CanvasRenderingContext2D,
    { x, y, tileW, tileH }: TileDimensions,
  ) => {
    const gradient = ctx.createLinearGradient(x, y, x + tileW, y + tileH);

    gradient.addColorStop(0, "#1e3a5f");
    gradient.addColorStop(1, "#581c87");

    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, tileW, tileH);
  };

  // const drawPausedVideoTile = (
  //   ctx: CanvasRenderingContext2D,
  //   { x, y, tileW, tileH }: TileDimensions,
  //   radius: number,
  // ) => {
  //   const gradient = ctx.createLinearGradient(x, y, x + tileW, y + tileH);

  //   gradient.addColorStop(0, "#1e3a5f");
  //   gradient.addColorStop(1, "#581c87");

  //   ctx.fillStyle = gradient;

  //   roundedRectPath(ctx, x, y, tileW, tileH, radius);

  //   ctx.fill();

  //   const circleX = x + tileW / 2;
  //   const circleY = y + tileH / 2;
  //   const circleR = Math.min(tileW, tileH) * 0.15;

  //   ctx.beginPath();
  //   ctx.arc(circleX, circleY, circleR, 0, Math.PI * 2);

  //   ctx.fillStyle = "#3b82f6";
  //   ctx.fill();

  //   ctx.beginPath();

  //   ctx.arc(circleX, circleY - circleR * 0.25, circleR * 0.35, 0, Math.PI * 2);

  //   ctx.fillStyle = "rgba(255,255,255,0.9)";
  //   ctx.fill();

  //   ctx.beginPath();

  //   ctx.ellipse(
  //     circleX,
  //     circleY + circleR * 0.45,
  //     circleR * 0.45,
  //     circleR * 0.35,
  //     0,
  //     Math.PI,
  //     Math.PI * 2,
  //   );

  //   ctx.fill();
  // };

  const drawPausedVideoTile = (
    ctx: CanvasRenderingContext2D,
    { x, y, tileW, tileH }: TileDimensions,
    radius: number,
    role?: string,
  ) => {
    const gradient = ctx.createLinearGradient(x, y, x + tileW, y + tileH);

    gradient.addColorStop(0, "#1e3a5f");
    gradient.addColorStop(1, "#581c87");

    ctx.fillStyle = gradient;

    roundedRectPath(ctx, x, y, tileW, tileH, radius);

    ctx.fill();

    const circleX = x + tileW / 2;
    const circleY = y + tileH / 2;
    const circleR = Math.min(tileW, tileH) * 0.15;

    const avatarImage =
      role === "Judge" ? judgeAvatarRef.current : silhouetteAvatarRef.current;

    if (avatarImage && avatarImage.complete && avatarImage.naturalWidth > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(circleX, circleY, circleR, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(
        avatarImage,
        circleX - circleR,
        circleY - circleR,
        circleR * 2,
        circleR * 2,
      );
      ctx.restore();
      return;
    }

    const fallbackR = Math.min(tileW, tileH) * 0.15;

    ctx.beginPath();
    ctx.arc(circleX, circleY, fallbackR, 0, Math.PI * 2);
    ctx.fillStyle = "#3b82f6";
    ctx.fill();

    ctx.beginPath();
    ctx.arc(
      circleX,
      circleY - fallbackR * 0.25,
      fallbackR * 0.35,
      0,
      Math.PI * 2,
    );
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fill();

    ctx.beginPath();
    ctx.ellipse(
      circleX,
      circleY + fallbackR * 0.45,
      fallbackR * 0.45,
      fallbackR * 0.35,
      0,
      Math.PI,
      Math.PI * 2,
    );
    ctx.fill();
  };

  const getOrCreateFrozenCanvas = (
    video: HTMLVideoElement,
    index: number,
    tileW: number,
    tileH: number,
    frozenFramesRef: React.RefObject<Map<number, HTMLCanvasElement>>,
  ) => {
    let frozenCanvas = frozenFramesRef.current.get(index);

    if (!frozenCanvas && video.readyState >= 2) {
      frozenCanvas = document.createElement("canvas");

      frozenCanvas.width = Math.round(tileW);
      frozenCanvas.height = Math.round(tileH);

      const fCtx = frozenCanvas.getContext("2d");

      if (fCtx) {
        fCtx.drawImage(video, 0, 0, frozenCanvas.width, frozenCanvas.height);
      }

      frozenFramesRef.current.set(index, frozenCanvas);
    }

    return frozenCanvas;
  };

  const drawFrozenLabel = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    tileW: number,
    tileH: number,
  ) => {
    const labelW = 120;
    const labelH = 30;
    const labelX = x + tileW / 2 - labelW / 2;
    const labelY = y + 12;

    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.beginPath();

    if (ctx.roundRect) {
      ctx.roundRect(labelX, labelY, labelW, labelH, 6);
    } else {
      ctx.rect(labelX, labelY, labelW, labelH);
    }

    ctx.fill();

    ctx.font = `bold ${Math.max(13, tileH * 0.025)}px sans-serif`;
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.fillText("❄ Frozen", x + tileW / 2, labelY + labelH / 2);
  };

  const updateFrozenFrame = (
    video: HTMLVideoElement,
    index: number,
    tileW: number,
    tileH: number,
    frozenFramesRef: FrozenFramesRef,
  ) => {
    let frozenCanvas = frozenFramesRef.current.get(index);

    if (!frozenCanvas) {
      frozenCanvas = document.createElement("canvas");

      frozenCanvas.width = Math.round(tileW);
      frozenCanvas.height = Math.round(tileH);

      frozenFramesRef.current.set(index, frozenCanvas);
    }

    const fCtx = frozenCanvas.getContext("2d");

    if (fCtx) {
      fCtx.drawImage(video, 0, 0, frozenCanvas.width, frozenCanvas.height);
    }
  };

  const drawBlurredVideo = (
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    x: number,
    y: number,
    tileW: number,
    tileH: number,
  ) => {
    ctx.drawImage(video, x, y, tileW, tileH);

    ctx.filter = "blur(40px)";
    ctx.drawImage(video, x - 40, y - 40, tileW + 80, tileH + 80);

    ctx.filter = "blur(20px)";
    ctx.drawImage(video, x - 20, y - 20, tileW + 40, tileH + 40);

    ctx.filter = "none";
  };

  const drawVideoContent = (
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    stream: StreamItem,
    dimensions: TileDimensions,
    radius: number,
    index: number,
    frozenFramesRef: FrozenFramesRef,
  ) => {
    const { x, y, tileW, tileH } = dimensions;

    if (stream?.videoPaused) {
      drawPausedVideoTile(ctx, dimensions, radius, stream?.role);
      return;
    }

    if (stream?.frozen) {
      const frozenCanvas = getOrCreateFrozenCanvas(
        video,
        index,
        tileW,
        tileH,
        frozenFramesRef,
      );

      if (frozenCanvas) {
        ctx.drawImage(frozenCanvas, x, y, tileW, tileH);
      } else {
        drawGradientPlaceholder(ctx, dimensions);
      }

      drawFrozenLabel(ctx, x, y, tileW, tileH);

      return;
    }

    if (video.readyState >= 2) {
      updateFrozenFrame(video, index, tileW, tileH, frozenFramesRef);

      if (stream?.blurred) {
        drawBlurredVideo(ctx, video, x, y, tileW, tileH);
      } else {
        ctx.drawImage(video, x, y, tileW, tileH);
      }

      return;
    }

    const frozenCanvas = frozenFramesRef.current.get(index);

    if (frozenCanvas) {
      ctx.drawImage(frozenCanvas, x, y, tileW, tileH);
    } else {
      drawGradientPlaceholder(ctx, dimensions);
    }
  };
  const drawFrame = () => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    const currentStreams = streamsRef.current;
    const videos = videoRefs.current;
    const liveOtherStreams = currentStreams.filter((s) => !s.spotlight);
    const count = liveOtherStreams.length;

    if (count > 0) {
      const cols = Math.ceil(Math.sqrt(count));
      const rows = Math.ceil(count / cols);

      const margin = 2;
      const radius = 8;

      const rawTileW = width / cols;
      const rawTileH = height / rows;

      const tileW = rawTileW - margin * 2;
      const tileH = rawTileH - margin * 2;

      for (let index = 0; index < count; index++) {
        const video = videos[index];
        if (!video) continue;

        const col = index % cols;
        const row = Math.floor(index / cols);

        const x = col * rawTileW + margin;
        const y = row * rawTileH + margin;

        const dimensions: TileDimensions = { x, y, tileW, tileH };
        const stream = liveOtherStreams[index];

        ctx.save();
        roundedRectPath(ctx, x, y, tileW, tileH, radius);
        ctx.clip();
        ctx.filter = "none";
        ctx.textAlign = "left";

        drawVideoContent(
          ctx,
          video,
          stream,
          dimensions,
          radius,
          index,
          frozenFramesRef,
        );

        ctx.restore();

        ctx.lineWidth = 4;
        ctx.strokeStyle = "rgba(0,0,0,0.55)";
        roundedRectPath(ctx, x, y, tileW, tileH, radius);
        ctx.stroke();

        const participantName = stream
          ? getParticipantDisplayName(stream)
          : "Participant";
        drawAutoBox({
          ctx,
          text: participantName,
          x,
          y: y + tileH - 40,
          font: "22px sans-serif",
          paddingX: 20,
          paddingY: 10,
          radius,
        });
      }
    }

    // Cleanup removed frozen frames
    frozenFramesRef.current.forEach((_, index) => {
      if (index >= count) {
        frozenFramesRef.current.delete(index);
      }
    });

    // Logo overlay
    if (logoRef.current) {
      ctx.drawImage(logoRef.current, 20, 20, 110, 110);
    }

    const courtData = courtRoomDataRef.current;

    const roomLabel =
      `${courtData?.courtRoomNo ?? ""} ${courtData?.courtRoomName ?? ""}`.trim();
    /**
     * Breaks a single word that is wider than maxWidth into chunks.
     * All full chunks are pushed into `lines`; the last (partial) chunk is returned
     * so the caller can continue appending to it.
     */
    const breakLongWord = (
      ctx: CanvasRenderingContext2D,
      word: string,
      maxWidth: number,
      lines: string[],
    ): string => {
      let chunk = "";
      for (const char of word) {
        const testChunk = chunk + char;
        const overflows = ctx.measureText(testChunk).width > maxWidth;
        if (overflows && chunk) {
          lines.push(chunk);
          chunk = char;
        } else {
          chunk = testChunk;
        }
      }
      return chunk;
    };

    const wrapText = (
      ctx: CanvasRenderingContext2D,
      text: string,
      maxWidth: number,
    ): string[] => {
      const lines: string[] = [];
      let currentLine = "";

      for (const word of text.split(" ")) {
        if (ctx.measureText(word).width > maxWidth) {
          if (currentLine) lines.push(currentLine);
          currentLine = breakLongWord(ctx, word, maxWidth, lines);
          continue;
        }

        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (currentLine && ctx.measureText(testLine).width > maxWidth) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }

      if (currentLine) lines.push(currentLine);
      return lines;
    };

    // Date box (top-right) measured first so the room label can fit beside it
    const dateTime = getISTDateTime();
    ctx.font = "24px sans-serif";
    const dateBoxWidth = ctx.measureText(dateTime).width + 40;
    const dateBoxX = width - dateBoxWidth - 20;

    // Room label sits between the logo (20 + 110) and the date box, wrapping if too long
    const roomAreaLeft = logoRef.current ? 150 : 20;
    const roomAreaRight = dateBoxX - 20;
    const roomAreaWidth = Math.max(roomAreaRight - roomAreaLeft, 100);

    ctx.font = "26px sans-serif";
    const roomLabelLines = wrapText(ctx, roomLabel, roomAreaWidth - 40);

    const roomTextWidth =
      Math.max(...roomLabelLines.map((l) => ctx.measureText(l).width)) + 40;

    drawAutoBox({
      ctx,
      text: roomLabelLines,
      x: roomAreaLeft + (roomAreaWidth - roomTextWidth) / 2,
      y: 25,
      font: "26px sans-serif",

      paddingX: 20,
      paddingY: 8,
      radius: 5,
    });

    drawAutoBox({
      ctx,
      text: dateTime,
      x: dateBoxX,
      y: 20,
      font: "24px sans-serif",
      paddingX: 20,
      paddingY: 10,
      radius: 8,
    });

    // VideoGrid.tsx — replace the courtName/poweredBy block with guarded version
    const courtName = courtData?.highcourt?.trim() || "";
    const poweredBy = courtData?.xPoweredBy?.trim() || "";
    const footerLines = [courtName, poweredBy].filter(Boolean);

    if (footerLines.length > 0) {
      ctx.font = "20px sans-serif";
      const boxWidth =
        Math.max(...footerLines.map((l) => ctx.measureText(l).width)) + 40;

      drawAutoBox({
        ctx,
        text: footerLines,
        x: width - boxWidth - 20,
        y: height - 90,
        font: "20px sans-serif",
        paddingX: 20,
        paddingY: 10,
        radius: 8,
      });
    }

  };
  drawFrameRef.current = drawFrame;

  /**
   * Draw loop is driven by a Web Worker timer instead of requestAnimationFrame.
   * rAF is paused when the tab is hidden / window minimized, and main-thread
   * timers get throttled → canvas stops repainting → captureStream emits no
   * frames → MediaRecorder stops producing chunks. Worker timers are not
   * throttled in background tabs, so recording/streaming keeps flowing.
   */
  const startDrawLoop = () => {
    if (tickWorkerRef.current) return; // already running — never start twice
    const workerSrc = `
      let id = null;
      self.onmessage = (e) => {
        if (e.data === "stop") { clearInterval(id); id = null; return; }
        clearInterval(id);
        id = setInterval(() => self.postMessage("tick"), e.data);
      };
    `;
    const url = URL.createObjectURL(
      new Blob([workerSrc], { type: "application/javascript" }),
    );
    const worker = new Worker(url);
    URL.revokeObjectURL(url);
    worker.onmessage = () => drawFrameRef.current();
    worker.postMessage(1000 / FPS);
    tickWorkerRef.current = worker;
    drawFrameRef.current(); // paint first frame immediately
  };

  const stopDrawLoop = () => {
    if (tickWorkerRef.current) {
      tickWorkerRef.current.postMessage("stop");
      tickWorkerRef.current.terminate();
      tickWorkerRef.current = null;
    }
  };

  
useEffect(() => {
  if (userData?.role === "Judge") return;

  const roomId = courtRoomData?.courtRoomId || userData?.roomId;
  const roomNo = courtRoomData?.courtRoomNo || userData?.roomNo;

  if (!roomId) {
    console.log("[SYNC] roomId missing, skipping");
    return;
  }

  const sync = () => {
    socket.emit("get-actions", { roomId, roomNo }, (d: any) => {
      if (!d?.status) return;

      const live = !!d.streaming || d.streamingAction?.status === "live";
      setIsStreaming(live);
      setStreamingStartedAt(
        live
          ? toLocalTime(
              d.streamingStartedAt || d.streamingAction?.updatedAt,
              d.serverNow,
            ) || Date.now()
          : null,
      );

      if (d.recording) {
        setRecording("start");
        setActiveRecordingCnr(d.cnr || null);
        setRecordingStartedAt(
          toLocalTime(d.recordingStartedAt, d.serverNow) || Date.now(),
        );
      }
    });
  };

  sync();
  socket.on("connect", sync);
  return () => {
    socket.off("connect", sync);
  };
}, [
  userData?.role,
  userData?.roomId,
  userData?.roomNo,
  courtRoomData?.courtRoomId,
  courtRoomData?.courtRoomNo,
]);

  // ─── Recording ───────────────────────────────────────────────────────────
  const startRecording = ({
    cnr,
    roomId,
    courtRoomNo,
    actionBy,
    mode,
  }: any) => {
    const canvas = canvasRef.current;
    if (!canvas || !socket) return;

    // Duplicate start while a recorder is still running → stop the old one.
    // Its onstop sees it was superseded and skips record_stop / teardown.
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }

    // Store session params for socket reconnect recovery
    activeSessionRef.current = { cnr, roomId, courtRoomNo, actionBy };

    // Start draw loop BEFORE MediaRecorder so canvas has valid frames from frame 0
    startDrawLoop();

    const canvasStream = canvas.captureStream(FPS);

    // ── AudioContext — created ONCE, never recreated ──────────────────────
    // Recreating AudioContext resets its clock → audio timestamps jump
    // backwards → FFmpeg DTS error → stream corrupts on camera toggle.
    const audioCtx = new AudioContext();
    const audioDest = audioCtx.createMediaStreamDestination();
    audioCtxRef.current = audioCtx;
    audioDestRef.current = audioDest;
    audioSourcesRef.current.clear();

    // Connect all current video elements to the audio graph
    syncAudioSources();

    const mixedStream = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...audioDest.stream.getAudioTracks(),
    ]);

    // Codec — prefer vp9/opus WebM (best FFmpeg compatibility)
    let mimeType = "video/webm";
    if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")) {
      mimeType = "video/webm;codecs=vp9,opus";
    } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")) {
      mimeType = "video/webm;codecs=vp8,opus";
    }

    console.log("mimiety", mimeType);
    // Periodic keyframes help stream-backend recover if its HLS pipeline
    // crashes and is respawned mid-recording. Ignored where unsupported.
    const recorder = new MediaRecorder(mixedStream, {
      mimeType,
      videoBitsPerSecond: 1_500_000,
      videoKeyFrameIntervalDuration: 2000,
    } as MediaRecorderOptions);

    recordingSocket.emit("record_start", {
      sessionId: cnr,
      roomId,
      courtRoomNo,
      actionBy,
      mode,
    });
    console.log(
      `[startRecording] record_start emitted → sessionId: ${cnr}, mode: ${mode}`,
    );

    let firstChunkSent = false;

    // A newer recorder replaced this one (recording restarted before this
    // one finished stopping). Its leftovers must not touch the new session —
    // same CNR = same sessionId on stream-backend.
    const superseded = () =>
      recorderRef.current !== null && recorderRef.current !== recorder;

    recorder.ondataavailable = async (e) => {
      if (superseded()) return;
      if (e.data.size > 0) {
        const arrayBuffer = await e.data.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        recordingSocket.emit("record_chunk", {
          sessionId: cnr,
          chunk: uint8Array,
          isHeader: !firstChunkSent,
        });
        firstChunkSent = true;
      }
    };

    recorder.onstop = () => {
      // Restarted already → new record_start finalized the old backend
      // session; sending record_stop now would stop the NEW one, and the
      // teardown below would kill the new draw loop / AudioContext.
      if (superseded()) {
        audioCtx.close().catch(() => {});
        return;
      }

      recordingSocket.emit("record_stop", { sessionId: cnr });
      stopDrawLoop();

      // Tear down AudioContext cleanly
      audioSourcesRef.current.forEach((nodes) => {
        try {
          nodes.gain.disconnect();
          nodes.source.disconnect();
        } catch {}
      });
      audioSourcesRef.current.clear();
      void audioCtxRef.current?.close();
      audioCtxRef.current = null;
      audioDestRef.current = null;
      activeSessionRef.current = null;
    };

    recorder.start(250);
    recorderRef.current = recorder;
    (recorderRef.current as any).sessionId = cnr;
  };

  const stopRecording = () => {
    if (!recorderRef.current) return;
    recorderRef.current.stop();
    recorderRef.current = null;
  };

  // ─── Socket reconnect recovery ────────────────────────────────────────────
  // When 4002 socket drops and reconnects, re-announce the active session
  // so the backend starts accepting record_chunk events again.
  useEffect(() => {
    const onReconnect = () => {
      const session = activeSessionRef.current;
      if (!session) return;
      console.log(
        "[recordingSocket] reconnected — recovering session:",
        session.cnr,
      );
      recordingSocket.emit("record_start", { ...session, isRecovery: true });
    };

    recordingSocket.on("reconnect", onReconnect);
    return () => {
      recordingSocket.off("reconnect", onReconnect);
    };
  }, []);

  // ─── Keep AudioContext alive across tab switch / minimize ────────────────
  useEffect(() => {
    const resumeAudio = () => {
      const ctx = audioCtxRef.current;
      if (ctx?.state === "suspended") ctx.resume().catch(() => {});
    };
    document.addEventListener("visibilitychange", resumeAudio);
    return () => {
      document.removeEventListener("visibilitychange", resumeAudio);
    };
  }, []);

  // ─── Recording action listener ────────────────────────────────────────────
  useEffect(() => {
    logger.log("Recording UseEffect");

    const isRecordingActionCurrent = (reAction: any, currentRoom: any) => {
      if (
        reAction?.roomId &&
        currentRoom?.courtRoomId &&
        String(reAction.roomId) !== String(currentRoom.courtRoomId)
      ) {
        return false;
      }

      if (reAction.status !== "stop" || !reAction.cnr) return true;

      const activeRecordingCnr =
        (recorderRef.current as any)?.sessionId ||
        activeSessionRef.current?.cnr ||
        selectedCaseRef.current?.cnr;

      return (
        !activeRecordingCnr ||
        String(reAction.cnr) === String(activeRecordingCnr)
      );
    };

    const handleRecordingStart = (reAction: any) => {
      const currentRoom = courtRoomDataRef.current;
      if (!isRecordingActionCurrent(reAction, currentRoom)) return;

      setRecording(reAction.status);
      setActiveRecordingCnr(
        reAction.status === "start"
          ? reAction.cnr || selectedCaseRef.current?.cnr || null
          : null,
      );
      setRecordingStartedAt(
        reAction.status === "start"
          ? toLocalTime(reAction.startedAt, reAction.serverNow) || Date.now()
          : null,
      );

      if (reAction.status === "start" && userRoleRef.current === "Judge") {
        const cnr = reAction.cnr || selectedCaseRef.current?.cnr;
        if (!cnr) return;
        startRecording({
          cnr,
          roomId: currentRoom?.courtRoomId,
          courtRoomNo: currentRoom?.courtRoomNo,
          actionBy: reAction.userId,
          mode: reAction.mode ?? "record",
        });
      }
      if (reAction.status === "stop" && userRoleRef.current === "Judge") {
        console.log(
          `[recording-action-updated] Stopping → mode: ${reAction.mode}`,
        );
        stopRecording();
      }
      if (reAction.status === "stop") {
        setIsStreaming(false);
        setStreamingStartedAt(null);
      }
    };

    const handleStreamingUpdated = (streamAction: any) => {
      const currentRoom = courtRoomDataRef.current;

      if (
        streamAction?.roomId &&
        currentRoom?.courtRoomId &&
        String(streamAction.roomId) !== String(currentRoom.courtRoomId)
      ) {
        return;
      }

      const isLive = streamAction.status === "resume";
      setIsStreaming(isLive);
      setStreamingStartedAt(
        isLive
          ? toLocalTime(streamAction.startedAt, streamAction.serverNow) ||
              Date.now()
          : null,
      );
    };

    socket.on("recording-action-updated", handleRecordingStart);
    socket.on("streaming-action-updated", handleStreamingUpdated);
    return () => {
      socket.off("recording-action-updated", handleRecordingStart);
      socket.off("streaming-action-updated", handleStreamingUpdated);
    };
  }, [socket]);

  // ── REC timer — start/stop with recording ──
  useEffect(() => {
    if (recording === "start") {
      setRecSeconds(
        recordingStartedAt
          ? Math.max(0, Math.floor((Date.now() - recordingStartedAt) / 1000))
          : 0,
      );
      recTimerRef.current = setInterval(() => {
        if (recordingStartedAt) {
          setRecSeconds(
            Math.max(0, Math.floor((Date.now() - recordingStartedAt) / 1000)),
          );
        } else {
          setRecSeconds((prev) => prev + 1);
        }
      }, 1000);
    } else {
      if (recTimerRef.current) {
        clearInterval(recTimerRef.current);
        recTimerRef.current = null;
      }
      setRecSeconds(0);
    }

    return () => {
      if (recTimerRef.current) {
        clearInterval(recTimerRef.current);
      }
    };
  }, [recording, recordingStartedAt]);

  // ─── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopDrawLoop();
      if (recorderRef.current) {
        recorderRef.current.stop();
        recorderRef.current = null;
      }
      audioSourcesRef.current.forEach((nodes) => {
        try {
          nodes.gain.disconnect();
          nodes.source.disconnect();
        } catch {}
      });
      void audioCtxRef.current?.close();
    };
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {spotlightStream && (
        <BigVideo
          stream={spotlightStream}
          handRaised={raisedHands.some(
            (hand) => hand.userId === spotlightStream.userId,
          )}
        />
      )}

      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="hidden"
      />
      <div className="relative h-full min-h-0 overflow-y-auto">
        {/* REC Badge — top left */}
        <div className="absolute top-3 left-3 flex flex-col gap-2 pointer-events-none z-50">
          {recording === "start" && (
            <div className="flex items-center gap-1.5 bg-black/70 px-3 py-1.5 rounded-full">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse inline-block" />
              <span className="text-white text-xs font-bold tracking-widest">
                {" "}
                REC {formatRecTime(recSeconds)}{" "}
              </span>
            </div>
          )}

          {isStreaming && (
            <div className="flex items-center gap-1.5 bg-black/70 px-3 py-1.5 rounded-full">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse inline-block" />
              <span className="text-white text-xs font-bold tracking-widest">
                LIVE
              </span>
            </div>
          )}
        </div>
        <div
          className={`grid ${getGridClasses(otherStreams.length)} min-h-full auto-rows-fr gap-1`}
        >
          {otherStreams.map((s, i) => (
            <VideoTile
              key={s.userId ?? i} // stable key — prevents VideoTile remount on re-render
              stream={s.stream}
              peer={s}
              handRaised={raisedHands.some((hand) => hand.userId === s.userId)}
              videoRefs={videoRefs}
              index={i}
              activeDeviceSelector={activeDeviceSelector}
              setActiveDeviceSelector={setActiveDeviceSelector}
            />
          ))}
        </div>
      </div>
    </>
  );
}

const getGridClasses = (count: number) => {
  if (count === 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-1 md:grid-cols-2";
  if (count === 3) return "grid-cols-1 sm:grid-cols-2";
  if (count <= 4) return "grid-cols-2";
  if (count <= 6) return "grid-cols-2 lg:grid-cols-3";
  if (count <= 9) return "grid-cols-2 md:grid-cols-3";
  if (count <= 12) return "grid-cols-3 lg:grid-cols-4";
  return "grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";
};
